import React, { useEffect, useState, useMemo } from "react";
import Fuse from "fuse.js";
import { capitalize } from "lodash";
import cx from "classnames";
import { Box, Icon, Input, Select, SelectOption, Text, Tooltip } from "@showitinc/showit-wc";

import Icons from "../../../../constants/Icons";
import {
  ALL_ICONS,
  BIG_ICONS,
  CUSTOM_ICON_CONTENT,
  ICON_CONTENT,
} from "../../../../constants/IconConstants";
import AppConstants from "../../../../constants/AppConstants";
import styles from "./IconChooserProperties.module.css";

const { ICON_LIST, ICON_TAG_LIST } = Icons;
const { UPDATE_ACTION_DELETE } = AppConstants;

const VALID_ICONS = ICON_LIST.filter((current) => !current.hideInApp);
const SEARCH_CONFIG = {
  includeScore: true,
  threshold: 0.4,
  keys: ["name"],
};

const IconCard = ({ big, name, selected, svg, onSelect }) => (
  <div className={cx([styles["icon-wrapper"], { [styles.selected]: selected }])} onClick={onSelect}>
    <div
      className={cx([styles.icon, { [styles.big]: big }, "tooltip-target"])}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  </div>
);

const IconSearch = ({ inputValue, setInputValue, updateList }) => {
  const fuse = new Fuse(VALID_ICONS, SEARCH_CONFIG);

  useEffect(() => {
    const searchResult = fuse.search(inputValue);
    const result = searchResult.map(({ item }) => item);

    updateList(!result.length && !inputValue ? undefined : result);
  }, [inputValue]);

  return (
    <div className={styles["search-input-container"]}>
      <Icon type="search" size="20" className={styles['search-icon']} />
      <Input
        className={styles["search-input"]}
        fullWidth
        hideLabel
        onSIInput={(e) => setInputValue(e?.detail?.value || "")}
        value={inputValue}
      />
    </div>
  );
};

const ElementPropertiesIconChooser = ({ element, isLocked, onPropUpdate }) => {
  const [filter, setFilter] = useState(ALL_ICONS);
  const [inputValue, setInputValue] = useState("");
  const [searchList, setSearchList] = useState(undefined);

  const _onPropChange = (propInfo) => {
    onPropUpdate && onPropUpdate(propInfo);
  };

  const _onCategoryChange = ({ detail }) => {
    setFilter(detail?.value || ALL_ICONS);
    setInputValue("");
    setSearchList(undefined);
  };

  const _selectIcon = (id) => {
    if (!isLocked) {
      _onPropChange([
        {
          name: ICON_CONTENT,
          value: id,
          addUndo: false,
        },
        {
          name: CUSTOM_ICON_CONTENT,
          value: null,
          action: UPDATE_ACTION_DELETE,
          addUndo: false,
        },
      ]);
    }
  };

  useEffect(() => {
    inputValue && filter !== ALL_ICONS && setFilter(ALL_ICONS);
  }, [inputValue, filter]);

  const _getIconThumbs = () => {
    const selected = element.getIn(ICON_CONTENT.split("."));
    return (searchList || VALID_ICONS).reduce(
      (result, { name, id, svg, tags = [] }) =>
        (searchList ? true : filter === ALL_ICONS || tags.includes(filter))
          ? [
              ...result,
              <IconCard
                big={BIG_ICONS.includes(id)}
                key={id}
                name={name}
                onSelect={() => _selectIcon(id)}
                selected={selected === id}
                svg={svg}
              />,
            ]
          : result,
      []
    );
  };

  return (
    <>
      <Box className={styles["icon-chooser-container"]}>
        <IconSearch
          updateList={setSearchList}
          inputValue={inputValue}
          setInputValue={setInputValue}
        />
        <Select
          alwaysEmit
          disabled={isLocked}
          hideLabel
          minParent
          onSISelect={_onCategoryChange}
          value={filter}
        >
          {[ALL_ICONS, ...ICON_TAG_LIST].map((tag) => (
            <SelectOption key={tag} value={tag}>
              {capitalize(tag)}
            </SelectOption>
          ))}
        </Select>
      </Box>
      <div className={styles["icon-chooser"]}>
        {useMemo(_getIconThumbs, [searchList, filter, element])}
      </div>
    </>
  );
};

export default ElementPropertiesIconChooser;
